import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { MaskedLines, Rule, FadeUp } from '../components/anim';
import { CHAPTERS, SPECIALTIES, CONTACT, TITLE, UI } from '../data/portfolio';
import { useLang } from '../i18n';

export default function Home() {
  const { t } = useLang();
  const bandRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: bandRef, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.03]);

  const scrollToWork = () => {
    if (window.__lenis) window.__lenis.scrollTo('#selected', { duration: 1.4 });
    else document.getElementById('selected')?.scrollIntoView({ behavior: 'smooth' });
  };

  const quote = {
    en: 'Architecture starts when you carefully put two bricks together. There it begins.',
    fr: 'L’architecture commence lorsque l’on assemble soigneusement deux briques. C’est là qu’elle commence.',
    author: 'Mies van der Rohe',
  };

  return (
    <main data-testid="home-page">
      {/* Hero — the book cover, kinetic */}
      <section className="relative flex min-h-screen flex-col justify-center px-6 pt-24 md:px-10">
        <div className="mx-auto w-full max-w-[1600px]">
          <MaskedLines
            className="md:mt-12"
            delay={0.3}
            stagger={0.14}
            lines={[
              <h1
                key="n"
                data-testid="hero-name"
                className="text-[min(9.1vw,44px)] font-light uppercase leading-[1.02] tracking-[0.06em] md:text-[clamp(40px,4.45vw,64px)]"
              >
                Donatien Grimaud
              </h1>,
            ]}
          />
          <Rule delay={0.9} className="mt-8 w-full md:mt-12" />
          <MaskedLines
            className="mt-6 md:mt-8"
            delay={1.05}
            lines={[
              <p key="t" className="text-xs uppercase tracking-[0.35em] md:text-sm">
                {TITLE}
              </p>,
            ]}
          />
        </div>

        <div className="absolute inset-x-0 bottom-8 px-6 md:bottom-10 md:px-10">
          <div className="mx-auto flex max-w-[1600px] items-end justify-between">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="max-w-[60%] text-[9px] uppercase leading-relaxed tracking-[0.3em] text-ink/60 md:text-[11px]"
            >
              {SPECIALTIES}
            </motion.p>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 1 }}
              onClick={scrollToWork}
              data-testid="hero-scroll-btn"
              className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-ink/60 transition-colors hover:text-ink"
            >
              {t(UI.scroll)}
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}>
                <ArrowDown size={14} strokeWidth={1.25} />
              </motion.span>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Parallax editorial band */}
      <section ref={bandRef} className="px-6 md:px-10">
        <div className="mx-auto max-w-[1600px]">
          <div className="relative h-[58vh] overflow-hidden bg-paper md:h-[86vh]">
            <motion.div
              className="absolute inset-0"
              initial={{ scale: 1.06 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            >
              <motion.img
                src="/work/hp-8.jpg"
                alt="Haute Parfumerie"
                data-testid="hero-parallax-image"
                style={{ y, scale }}
                className="h-[116%] w-full object-cover"
              />
            </motion.div>
          </div>
          <FadeUp className="mt-3 flex items-center text-[10px] uppercase tracking-[0.25em] text-ink/60">
            <span>HAUTE PARFUMERIE - VIC LOUNGE</span>
          </FadeUp>
        </div>
      </section>

      {/* Christian Dior quote */}
      <section className="px-6 py-24 md:px-10 md:py-40">
        <div className="mx-auto max-w-4xl text-center">
          <FadeUp>
            <p className="text-xl italic leading-snug text-pretty md:text-3xl" style={{ fontFamily: "'Gotham', 'Jost', sans-serif", fontStyle: 'italic', fontWeight: 300 }}>
              {t(quote)}
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-ink/50">{quote.author}</p>
          </FadeUp>
        </div>
      </section>

      {/* Chapters — title + image */}
      <section id="selected" className="px-6 pb-24 md:px-10 md:pb-40">
        <div className="mx-auto max-w-[1600px]">
          <div className="mt-8">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.slug} delay={i * 0.04}>
                <Link
                  to={`/work/${c.slug}`}
                  data-testid={`home-chapter-row-${c.slug}`}
                  className="group grid h-28 grid-cols-12 items-center gap-4 border-t border-ink/15 py-3 transition-colors duration-500 md:h-48"
                >
                  <h3 className="col-span-9 text-xl font-light uppercase tracking-[0.12em] transition-transform duration-500 group-hover:translate-x-2 md:col-span-9 md:text-5xl">
                    {c.title}
                  </h3>
                  <div className="col-span-3 h-full overflow-hidden bg-paper">
                    <img
                      src={c.cover}
                      alt={c.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
          <div className="border-t border-ink/15" />
        </div>
      </section>

      {/* Closing */}
      <section className="border-t border-ink/10 px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-10 md:flex-row md:items-end">
          <FadeUp>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="home-contact-cta"
              className="mt-4 block text-base uppercase md:text-2xl"
            >
              {CONTACT.email}
            </a>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link
              to="/contact"
              data-testid="home-contact-link"
              className="inline-block border border-ink px-8 py-4 text-[10px] uppercase tracking-[0.3em] transition-colors duration-500 hover:bg-ink hover:text-paper"
            >
              Contact
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
